/**
 * Shared types + user-facing copy for the lock-authorization surfaces
 * (admin-locked-assessments Parts 6/7/8). Copy is centralized so the admin and
 * teacher surfaces stay verbatim-consistent. All strings are neutral — never
 * "lock" / TIA — per references/ui-patterns.md.
 */

/** Resolved lock-authorization state for an assignment / classroom / student. */
export type AuthorizationState = 'authorized' | 'not_authorized';

/**
 * Feature flag gating the locked-assignment (lock-authorization) experience: the "Authorize student
 * access" toggle in the assignment builder + the authorized / not-authorized status in assignment
 * lists. Kept separate from `admin_managing_restrictions` (which gates the content-lock indicators)
 * so this still-incomplete flow — the teacher side in particular — can ship dark independently.
 */
export const CONTENT_RESTRICTIONS_PHASE_2_FLAG = 'content_restrictions_phase_2';

export const LOCK_AUTH_COPY = {
  /** The settings-form toggle (no section header — it's a single setting row). */
  settingLabel: 'Authorize student access to restricted content',
  settingHelp: 'Restricted content stays hidden from teachers.',

  /** Indicator chip labels. */
  authorized: 'Authorized',
  notAuthorized: 'Not authorized',
  overridden: 'Overridden',

  /** Admin, setting off → students are blocked until the admin grants. */
  adminBlockedWarning:
    "This assignment contains restricted questions. Students can't see them unless you authorize access.",

  /** Teacher (read-only) messaging. */
  teacherBlockedWarning:
    "Students can't access the restricted questions yet — an admin needs to grant access.",
  teacherAuthorizedConfirm:
    'Students in this class can open the restricted questions in this assignment.',
  requestAccess: 'Request access'
} as const;

/**
 * Maps the backend resolved-lock-authorization string (authorized / not_authorized / none) to the
 * binary chip state, or null to hide the chip. undefined (field omitted when the admin-restrictions
 * flag is off) and "none" hide it.
 */
export function resolvedLockAuthorizationToState(
  value: string | null | undefined
): AuthorizationState | null {
  switch (value) {
    case 'authorized':
      return 'authorized';
    case 'not_authorized':
      return 'not_authorized';
    default:
      return null;
  }
}

/** One scope's stored lock-authorization + the record id to target its set mutation. */
export interface ScopeLockAuthorization {
  /** ClassroomAssignment / StudentAssignment id — the `id` arg for the per-scope set mutation. */
  recordId: string;
  /** Stored value. `null` on a student row means "no override" → resolve up to the classroom value. */
  lockAuthorized: boolean | null;
}

/** An assignment's lock-authorization across all three scopes, keyed for populate + write. */
export interface LockAuthorizationSnapshot {
  /** Assignment-level (class-add) default; undefined when unreadable / not yet created. */
  assignmentDefault: boolean | undefined;
  /** classroomId → {recordId: classroomAssignmentId, lockAuthorized}. */
  classrooms: Map<string, ScopeLockAuthorization>;
  /** studentId → {recordId: studentAssignmentId, lockAuthorized}. */
  students: Map<string, ScopeLockAuthorization>;
}

export const EMPTY_LOCK_AUTHORIZATION_SNAPSHOT: LockAuthorizationSnapshot = {
  assignmentDefault: undefined,
  classrooms: new Map(),
  students: new Map()
};

/**
 * Effective lock authorization for a scope, resolving the three-level hierarchy the backend uses:
 * student override → classroom value → assignment default → false. Pure (no Apollo) so the settings
 * form's populate util and the submit path can both use it.
 */
export function resolveScopeLockAuthorization(
  snapshot: LockAuthorizationSnapshot,
  {classroomId, studentId}: {classroomId?: string; studentId?: string}
): boolean {
  if (studentId) {
    const studentValue = snapshot.students.get(studentId)?.lockAuthorized;
    if (studentValue != null) {
      return studentValue;
    }
  }
  if (classroomId) {
    const classroomValue = snapshot.classrooms.get(classroomId)?.lockAuthorized;
    if (classroomValue != null) {
      return classroomValue;
    }
  }
  return snapshot.assignmentDefault ?? false;
}
